import React, { useCallback, useEffect, useRef, useState } from 'react';

import TpDialog from '../shared/tp_dialog';
import EntryFields from './entry_fields';
import { FIELD_ERROR_TEXT } from './adapters';
import { edit_changed, edit_form, validate_form } from './form_logic';
import { LIMITS } from './params';

/*
 * PROD-7828 R8 — edit an entry. Teacher and payment month are immutable (Q13: remove and re-add),
 * and a reason is ALWAYS required (Q12) because the note is teacher-facing. The row's version goes
 * with the request; a VERSION_CONFLICT is surfaced by the tab, which reloads the list.
 */
const EditModal = ({
  row,
  categories,
  categories_error,
  on_retry_categories,
  limits,
  saving,
  error_text,
  now,
  search_sessions,
  on_submit,
  on_close,
  on_focus_lost
}) => {
  const [form, setForm] = useState(() => edit_form(row.row));
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setForm(edit_form(row.row));
    setReason('');
    setErrors({});
    setTouched(false);
  }, [row]);

  // Stable per teacher: the picker re-runs its lookup whenever this identity changes.
  const search_teacher_sessions = useCallback(
    (q) => search_sessions(form.teacher_uuid, q),
    [search_sessions, form.teacher_uuid]
  );

  const set_field = useCallback((key, value) => setForm((f) => ({ ...f, [key]: value })), []);

  // Try again unmounts with the error; focus moves to the category it unblocks, never to the page body.
  const box_ref = useRef(null);
  const had_categories_error = useRef(!!categories_error);
  useEffect(() => {
    if (had_categories_error.current && !categories_error) {
      const select = box_ref.current && box_ref.current.querySelector('#tp-ap-edit-category');
      if (select) select.focus();
    }
    had_categories_error.current = !!categories_error;
  }, [categories_error]);

  const changed = edit_changed(form, row.row);
  const submit_off = saving || !!categories_error || (touched && !changed);

  const submit = () => {
    setTouched(true);
    // Nothing changed = nothing to send: the server would write nothing and it must not read as "Saved".
    if (!changed) return;
    const result = validate_form(form, { now: now(), categories, mode: 'edit', reason, limits });
    setErrors(result.errors);
    if (!result.ok) return;
    on_submit(form, reason);
  };

  return (
    <TpDialog
      title_id="tp-ap-edit-title"
      busy={saving}
      on_close={on_close}
      on_focus_lost={on_focus_lost}
    >
      <div className="tp-modal tp-modal-wide" ref={box_ref}>
        <h4 id="tp-ap-edit-title">Edit additional payment</h4>
        <div className="tp-ctx">
          <b>{row.teacher_name}</b> · {row.month_text} · {row.category_label} · {row.amount_text}
          <div>
            Teacher and payment month cannot be changed — remove this entry and add a new one.
          </div>
        </div>

        {categories_error ? (
          <div className="tp-banner tp-banner-error" role="alert">
            {categories_error}{' '}
            <button type="button" className="tp-link" onClick={on_retry_categories}>
              Try again
            </button>
          </div>
        ) : null}

        <EntryFields
          id_prefix="tp-ap-edit"
          form={form}
          set_field={set_field}
          errors={touched ? errors : {}}
          categories={categories}
          limits={limits}
          search_sessions={search_teacher_sessions}
          session_disabled={false}
        />

        <div className="tp-field">
          <label htmlFor="tp-ap-edit-reason">Reason for this edit (internal, required)</label>
          <textarea
            id="tp-ap-edit-reason"
            className="tp-textarea"
            maxLength={LIMITS.REASON_MAX_LEN}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why this entry is being changed"
          />
          {touched && errors.reason ? (
            <span className="tp-err">{FIELD_ERROR_TEXT[errors.reason]}</span>
          ) : null}
        </div>
        {touched && !changed ? (
          <div className="tp-banner tp-banner-info" role="status">
            Nothing to save — change a field first.
          </div>
        ) : null}
        {/* B7: a refusal raised while this modal is open renders here, not under the overlay. */}
        {error_text ? (
          <div className="tp-banner tp-banner-error" role="alert">
            {error_text}
          </div>
        ) : null}

        <div className="tp-modal-ft">
          {/* aria-disabled, not disabled: disabling the focused button drops focus (CD-114). */}
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={saving ? undefined : on_close}
            aria-disabled={saving ? 'true' : undefined}
          >
            Cancel
          </button>
          <button
            type="button"
            className="tp-btn"
            onClick={submit_off ? undefined : submit}
            aria-disabled={submit_off ? 'true' : undefined}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </TpDialog>
  );
};

export default EditModal;
